import React from 'react';

import { Box, Typography, Tooltip } from '@mui/material';

import { DownloadIcon, FileTypeIcon } from '@components/icons';
import { getMimeType } from '@utils/helpers';

import type { MaterialsProperties } from './Materials.types';

/**
 * Отображение материалов анализа с иконкой файла и tooltip.
 * Если передан onDownload — блок становится кликабельным для скачивания файла.
 */
export const Materials: React.FC<MaterialsProperties> = ({
  fileName,
  onDownload,
  sx,
  dataTest = 'materials',
}) => {
  // Обрезаем имя файла если больше 15 символов
  const MAX_FILENAME_LENGTH = 15;
  const truncatedFileName =
    fileName.length > MAX_FILENAME_LENGTH
      ? `${fileName.slice(0, MAX_FILENAME_LENGTH)}...`
      : fileName;

  const isLongFileName = fileName.length > MAX_FILENAME_LENGTH;
  const isDownloadable = Boolean(onDownload);
  const mimeType = getMimeType(fileName);

  return (
    <Box sx={sx} data-testid={dataTest}>
      <Typography
        variant='overline'
        data-testid={`${dataTest}_label`}
        sx={{
          color: 'text.secondary',
          fontWeight: 700,
          letterSpacing: 1,
          fontSize: '0.8rem',
        }}
      >
        Материалы
      </Typography>
      <Box
        data-testid={`${dataTest}_content`}
        onClick={onDownload}
        role={isDownloadable ? 'button' : undefined}
        sx={{
          mt: { xs: 0.5, md: 1 },
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          ...(isDownloadable && {
            cursor: 'pointer',
            borderRadius: 1,
            px: 1,
            mx: -1,
            py: 0.5,
            transition: 'background-color 0.2s ease',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }),
        }}
      >
        <FileTypeIcon
          fileType={mimeType}
          styles={{
            fontSize: '1.5rem',
            pointerEvents: 'auto',
          }}
          sx={{ color: 'text.secondary', flexShrink: 0 }}
        />
        <Tooltip
          title={isDownloadable ? `Скачать ${fileName}` : fileName}
          disableHoverListener={!isLongFileName && !isDownloadable}
          arrow
          placement='top'
        >
          <Typography
            variant='body1'
            data-testid={`${dataTest}_filename`}
            sx={{
              fontSize: { xs: '0.8rem', md: '1rem' },
              fontWeight: 500,
              color: isDownloadable ? 'primary.main' : 'text.primary',
              wordBreak: 'break-word',
              cursor: isDownloadable
                ? 'pointer'
                : isLongFileName
                  ? 'help'
                  : 'default',
            }}
          >
            {truncatedFileName}
          </Typography>
        </Tooltip>
        {isDownloadable && (
          <DownloadIcon
            dataTest={`${dataTest}_download_icon`}
            sx={{ color: 'text.secondary', fontSize: '1.2rem', flexShrink: 0 }}
          />
        )}
      </Box>
    </Box>
  );
};
